import { useState, useEffect, useCallback } from "react";
import type { RoomServiceMenuSchedule } from "../types/roomServiceMenuSchedule";
import type { DayOfWeek } from "../types/restaurant";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAuthenticatedApi } from "./useAuthenticatedApi";
import { transformRoomServiceMenuSchedule } from "../api/roomServiceApi";
import { useScheduleIntervals } from "./useScheduleIntervals";

interface UseRoomServiceMenuSchedulesOptions {
  hotelId: string;
  roomServiceMenuId: string;
}

interface ScheduleInterval {
  intervalid: string;
  hotelid: string;
  starttime: string;
  endtime: string;
  description: string | null;
  createdat?: string;
  updatedat?: string;
}

// Explicit return type for the hook
interface UseRoomServiceMenuSchedulesReturn {
  schedules: RoomServiceMenuSchedule[];
  isLoading: boolean;
  error: Error | null;
  refetchSchedules: () => Promise<void>;
  hotelScheduleIntervals: ScheduleInterval[] | null;
  isLoadingHotelIntervals: boolean;
  intervalError: Error | null;
  upsertMenuSchedule: (
    day: DayOfWeek,
    openTime: string,
    closeTime: string,
    isActive: boolean,
    notes?: string | null,
    scheduletype?: string
  ) => Promise<void>;
  deleteRoomServiceMenuScheduleForDay: (day: DayOfWeek) => Promise<void>;
}

export const useRoomServiceMenuSchedules = ({
  hotelId,
  roomServiceMenuId,
}: UseRoomServiceMenuSchedulesOptions): UseRoomServiceMenuSchedulesReturn => {
  const { callApi } = useAuthenticatedApi();

  const [schedules, setSchedules] = useState<RoomServiceMenuSchedule[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Hotel-wide intervals handled via separate hook for better separation of concerns
  const {
    intervals: hotelScheduleIntervals,
    isLoading: isLoadingHotelIntervals,
    error: intervalError,
    findOrCreateIntervalId,
  } = useScheduleIntervals({ hotelId });

  const fetchSchedules = useCallback(async () => {
    if (!hotelId || !roomServiceMenuId) {
      setSchedules([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await callApi({
        method: "GET",
        url: `/management/hotel/room-service-menus/${roomServiceMenuId}/schedules`,
      });

      const rawSchedules = response.data.data || [];

      setSchedules(
        rawSchedules.map((s: any) => transformRoomServiceMenuSchedule(s))
      );
    } catch (err) {
      console.error("Failed to fetch room service menu schedules:", err);
      setError(
        err instanceof Error
          ? err
          : new Error(
              "An unknown error occurred while fetching room service menu schedules"
            )
      );
      setSchedules([]);
    } finally {
      setIsLoading(false);
    }
  }, [hotelId, roomServiceMenuId, callApi]);

  useEffect(() => {
    if (hotelId && roomServiceMenuId) {
      fetchSchedules();
    }
  }, [hotelId, roomServiceMenuId, fetchSchedules]);

  const upsertRoomServiceMenuSchedule = useCallback(
    async (
      day: DayOfWeek,
      openTime: string,
      closeTime: string,
      isActive: boolean,
      notes: string | null = null,
      scheduletype: string = "RoomServiceOperatingHours"
    ) => {
      if (!hotelId || !roomServiceMenuId) {
        throw new Error(
          "Missing IDs for upserting room service menu schedule."
        );
      }

      const targetIntervalId: string = await findOrCreateIntervalId(
        openTime,
        closeTime
      );

      const existingMenuScheduleForDay = schedules.find(
        (s) => s.dayOfWeek === day && s.roomServiceMenuId === roomServiceMenuId
      );

      // After determining intervalId, proceed with upsert
      try {
        if (existingMenuScheduleForDay?.rsMenuscheduleid) {
          // Update existing menu schedule entry
          await callApi({
            method: "PUT",
            url: `/management/hotel/room-service-menus/${roomServiceMenuId}/schedules/${existingMenuScheduleForDay.rsMenuscheduleid}`,
            data: {
              intervalid: targetIntervalId,
              dayofweek: day,
              isactive: isActive,
              scheduletype,
              notes,
            },
          });
        } else {
          // Create new menu schedule entry
          await callApi({
            method: "POST",
            url: `/management/hotel/room-service-menus/${roomServiceMenuId}/schedules`,
            data: {
              intervalid: targetIntervalId,
              dayofweek: day,
              isactive: isActive,
              scheduletype,
              notes,
            },
          });
        }

        // Refresh schedules after upsert
        await fetchSchedules();
      } catch (err) {
        console.error("Error upserting room service menu schedule:", err);
        throw err;
      }
    },
    [
      hotelId,
      roomServiceMenuId,
      schedules,
      findOrCreateIntervalId,
      callApi,
      fetchSchedules,
      hotelScheduleIntervals,
    ]
  );

  const deleteRoomServiceMenuScheduleForDay = useCallback(
    async (day: DayOfWeek) => {
      if (!hotelId || !roomServiceMenuId) {
        throw new Error("Missing IDs for deleting room service menu schedule.");
      }

      const scheduleToDelete = schedules.find(
        (s) =>
          s.dayOfWeek === day &&
          s.roomServiceMenuId === roomServiceMenuId &&
          s.isActive
      );

      if (!scheduleToDelete?.rsMenuscheduleid) {
        console.warn(`No active schedule found for day ${day} to deactivate.`);
        return;
      }

      try {
        // To "delete" or mark as inactive, we update with isActive: false
        await callApi({
          method: "PUT",
          url: `/management/hotel/room-service-menus/${roomServiceMenuId}/schedules/${scheduleToDelete.rsMenuscheduleid}`,
          data: {
            intervalid: scheduleToDelete.intervalid,
            dayofweek: day,
            isactive: false,
            scheduletype: scheduleToDelete.scheduletype ?? undefined,
            notes: scheduleToDelete.notes ?? undefined,
          },
        });

        // Refresh schedules after deactivation
        await fetchSchedules();
      } catch (err) {
        console.error("Error deactivating room service menu schedule:", err);
        throw new Error(
          `Failed to deactivate schedule for ${day}: ${
            err instanceof Error ? err.message : "Unknown error"
          }`
        );
      }
    },
    [hotelId, roomServiceMenuId, schedules, callApi, fetchSchedules]
  );

  return {
    schedules,
    isLoading,
    error,
    refetchSchedules: fetchSchedules,
    hotelScheduleIntervals,
    isLoadingHotelIntervals,
    intervalError,
    upsertMenuSchedule: upsertRoomServiceMenuSchedule,
    deleteRoomServiceMenuScheduleForDay,
  };
};
